/**
 * Validation. Deliberately NOT a full JSON Schema validator — there are good
 * ones (ajv) and we should use one in production. This is a fast, field-level
 * validator for the UI to show inline errors while typing, without shipping
 * ajv to the client for simple cases.
 *
 * For submit-time / server-side validation, wire in ajv behind the same
 * interface (see validate() below).
 */

import type { JsonSchema, JsonSchemaType, JsonValue } from '@bliksys/shared';

import { resolveSchema, type RefRegistry } from './resolver.js';
import { isPatternSafe } from './safe-regex.js';

/**
 * Upper bound on the string length we'll run a `pattern` against. Safe
 * (low star-height) patterns are linear, but this caps the residual risk
 * from shapes the safety check doesn't model (e.g. overlapping
 * alternation). No realistic constrained field is anywhere near this long.
 */
const MAX_PATTERN_INPUT_LENGTH = 100_000;

export interface ValidationIssue {
  path: string; // human-readable path like "pages[0].title"
  message: string;
}

/**
 * Run validation. The optional `registry` enables remote `$ref`
 * resolution; without it, remote refs throw `UnsupportedSchemaError`
 * inside `resolveSchema` and propagate up. Validate the schema
 * yourself before calling — this function trusts that the schema is
 * well-formed JSON.
 */
export function validate(
  value: JsonValue | undefined,
  schema: JsonSchema,
  rootSchema: JsonSchema,
  path = '',
  registry?: RefRegistry,
): ValidationIssue[] {
  const resolved = resolveSchema(schema, rootSchema, undefined, registry);
  const issues: ValidationIssue[] = [];

  // Empty/missing values are handled by the parent's `required` rule;
  // validation of a missing field below the parent is skipped. Note:
  // `null` is intentionally treated as "missing" rather than "the
  // value null" — pre-empt the nullable-type union case where the
  // parent's required-check is the gate.
  if (value === undefined || value === null) {
    // BUT: a schema that EXPLICITLY allows null (`type: 'null'` or
    // `type: ['T', 'null']`) should accept it without complaint. That's
    // the nullable case — fall through and let type-checking pass it.
    if (!schemaAllowsNull(resolved)) {
      return issues;
    }
    if (value === undefined) return issues;
    // value === null here, schema allows null → still skip non-type rules
    // (no point checking minLength etc on null).
    return runComposition(value, resolved, rootSchema, path, issues, registry);
  }

  // Effective declared types (handles nullable + multi-type unions).
  // Array-form `type: ['T', 'null']` means "T OR null"; the value here
  // is non-null (handled above), so we narrow to the non-null members.
  const declaredTypes = effectiveTypes(resolved.type);

  if (declaredTypes.length > 0) {
    // Find which declared type matches the JS type of the value. If
    // none matches, fail with a "wrong type" message that names the
    // expected types.
    const matchedType = declaredTypes.find((t) => valueIsOfType(value, t));
    if (matchedType === undefined) {
      issues.push({ path, message: expectedMessage(declaredTypes) });
    } else {
      // Run type-specific rules ONLY against the matched type. This is
      // the multi-type union path: `type: ['string', 'number']` with
      // value `42` validates against the number rules, not the string
      // rules.
      issues.push(...validateAgainstType(value, matchedType, resolved, rootSchema, path, registry));
    }
  } else {
    // No type declared — infer from shape based on which keywords
    // are present. JSON Schemas in the wild commonly omit `type`
    // when `properties` / `items` are given. The `required` keyword
    // alone is enough to trigger object-rule processing — used by
    // `then` / `else` schemas in if/then/else compositions.
    const looksLikeObject =
      resolved.properties !== undefined ||
      resolved.required !== undefined ||
      resolved.additionalProperties !== undefined;
    const looksLikeArray =
      resolved.items !== undefined ||
      resolved.minItems !== undefined ||
      resolved.maxItems !== undefined ||
      resolved.uniqueItems !== undefined ||
      resolved.contains !== undefined;
    if (looksLikeObject && typeof value === 'object' && !Array.isArray(value)) {
      issues.push(...validateAgainstType(value, 'object', resolved, rootSchema, path, registry));
    } else if (looksLikeArray && Array.isArray(value)) {
      issues.push(...validateAgainstType(value, 'array', resolved, rootSchema, path, registry));
    }
  }

  return runComposition(value, resolved, rootSchema, path, issues, registry);
}

/**
 * Type-specific rules (length, range, items, properties, etc.). Called
 * after type-checking has matched the value against one of the declared
 * types, so this function trusts that the value matches `type`.
 */
function validateAgainstType(
  value: JsonValue,
  type: JsonSchemaType,
  resolved: JsonSchema,
  rootSchema: JsonSchema,
  path: string,
  registry?: RefRegistry,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  switch (type) {
    case 'string': {
      const s = value as string;
      if (resolved.minLength !== undefined && s.length < resolved.minLength) {
        issues.push({ path, message: `Must be at least ${resolved.minLength} characters` });
      }
      if (resolved.maxLength !== undefined && s.length > resolved.maxLength) {
        issues.push({ path, message: `Must be at most ${resolved.maxLength} characters` });
      }
      if (resolved.pattern !== undefined) {
        // Refuse to run structurally dangerous patterns (ReDoS) or to test
        // an unbounded-length input. An unsafe/oversized pattern simply
        // isn't enforced rather than risking a CPU-pinning hang on the
        // anonymous /validate + /deploy endpoints. See safe-regex.ts.
        if (isPatternSafe(resolved.pattern) && s.length <= MAX_PATTERN_INPUT_LENGTH) {
          try {
            if (!new RegExp(resolved.pattern).test(s)) {
              issues.push({ path, message: 'Does not match the required format' });
            }
          } catch {
            // Malformed pattern — schema author's problem, not the user's.
          }
        }
      }
      if (resolved.format === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) {
        issues.push({ path, message: 'Must be a valid email address' });
      }
      break;
    }
    case 'number':
    case 'integer': {
      const n = value as number;
      if (type === 'integer' && !Number.isInteger(n)) {
        issues.push({ path, message: 'Must be a whole number' });
      }
      if (resolved.minimum !== undefined && n < resolved.minimum) {
        issues.push({ path, message: `Must be at least ${resolved.minimum}` });
      }
      if (resolved.maximum !== undefined && n > resolved.maximum) {
        issues.push({ path, message: `Must be at most ${resolved.maximum}` });
      }
      if (resolved.exclusiveMinimum !== undefined && n <= resolved.exclusiveMinimum) {
        issues.push({
          path,
          message: `Must be greater than ${resolved.exclusiveMinimum}`,
        });
      }
      if (resolved.exclusiveMaximum !== undefined && n >= resolved.exclusiveMaximum) {
        issues.push({
          path,
          message: `Must be less than ${resolved.exclusiveMaximum}`,
        });
      }
      if (resolved.multipleOf !== undefined && n % resolved.multipleOf !== 0) {
        issues.push({ path, message: `Must be a multiple of ${resolved.multipleOf}` });
      }
      break;
    }
    case 'boolean':
    case 'null':
      break;
    case 'array': {
      const arr = value as JsonValue[];
      if (resolved.minItems !== undefined && arr.length < resolved.minItems) {
        issues.push({ path, message: `Must have at least ${resolved.minItems} items` });
      }
      if (resolved.maxItems !== undefined && arr.length > resolved.maxItems) {
        issues.push({ path, message: `Must have at most ${resolved.maxItems} items` });
      }
      if (resolved.uniqueItems === true) {
        // Quadratic in array length. Acceptable for typical authoring
        // sizes (hundreds of items at most). Switch to a hash-based
        // approach if profiling says otherwise.
        const seen: JsonValue[] = [];
        for (const item of arr) {
          if (seen.some((s) => jsonEqual(s, item))) {
            issues.push({ path, message: 'List items must be unique' });
            break;
          }
          seen.push(item);
        }
      }
      if (resolved.items) {
        arr.forEach((item, i) => {
          issues.push(...validate(item, resolved.items!, rootSchema, `${path}[${i}]`, registry));
        });
      }
      // contains / minContains / maxContains: at least N items match.
      if (resolved.contains !== undefined) {
        let matchCount = 0;
        for (const item of arr) {
          if (validate(item, resolved.contains, rootSchema, '', registry).length === 0) {
            matchCount++;
          }
        }
        const min = resolved.minContains ?? 1;
        const max = resolved.maxContains;
        if (matchCount < min) {
          issues.push({
            path,
            message:
              min === 1
                ? 'Must contain at least one matching item'
                : `Must contain at least ${min} matching items`,
          });
        }
        if (max !== undefined && matchCount > max) {
          issues.push({
            path,
            message:
              max === 1
                ? 'Must contain at most one matching item'
                : `Must contain at most ${max} matching items`,
          });
        }
      }
      break;
    }
    case 'object': {
      const obj = value as Record<string, JsonValue>;
      for (const key of resolved.required ?? []) {
        if (
          obj[key] === undefined ||
          obj[key] === null ||
          (typeof obj[key] === 'string' && obj[key] === '')
        ) {
          issues.push({
            path: path ? `${path}.${key}` : key,
            message: 'This field is required',
          });
        }
      }
      for (const [key, propSchema] of Object.entries(resolved.properties ?? {})) {
        if (obj[key] !== undefined) {
          issues.push(
            ...validate(obj[key], propSchema, rootSchema, path ? `${path}.${key}` : key, registry),
          );
        }
      }
      // additionalProperties: false → reject extra keys not declared in `properties`.
      if (resolved.additionalProperties === false) {
        const declared = new Set(Object.keys(resolved.properties ?? {}));
        for (const key of Object.keys(obj)) {
          if (!declared.has(key)) {
            issues.push({
              path: path ? `${path}.${key}` : key,
              message: 'Unexpected property — not allowed by schema',
            });
          }
        }
      } else if (
        typeof resolved.additionalProperties === 'object' &&
        resolved.additionalProperties !== null
      ) {
        const declared = new Set(Object.keys(resolved.properties ?? {}));
        for (const [key, v] of Object.entries(obj)) {
          if (!declared.has(key)) {
            issues.push(
              ...validate(
                v,
                resolved.additionalProperties,
                rootSchema,
                path ? `${path}.${key}` : key,
                registry,
              ),
            );
          }
        }
      }
      break;
    }
  }
  return issues;
}

/**
 * Composition rules that run regardless of type: enum, const, not, and
 * if/then/else. Kept separate so the type-specific path doesn't have to
 * worry about them.
 */
function runComposition(
  value: JsonValue,
  resolved: JsonSchema,
  rootSchema: JsonSchema,
  path: string,
  issues: ValidationIssue[],
  registry?: RefRegistry,
): ValidationIssue[] {
  if (resolved.enum && resolved.enum.length > 0) {
    if (!resolved.enum.some((e) => jsonEqual(e, value))) {
      issues.push({ path, message: 'Not a permitted value' });
    }
  }
  if (resolved.const !== undefined) {
    if (!jsonEqual(resolved.const, value)) {
      issues.push({ path, message: 'Must be a specific value' });
    }
  }
  if (resolved.not !== undefined) {
    if (validate(value, resolved.not, rootSchema, '', registry).length === 0) {
      issues.push({ path, message: 'Value matches a forbidden shape (`not` constraint)' });
    }
  }
  // if/then/else: run `if` silently. If it passes, apply `then`; else apply `else`.
  // Both `then` and `else` are optional; if absent the corresponding branch is a no-op.
  if (resolved.if !== undefined) {
    const ifPassed = validate(value, resolved.if, rootSchema, '', registry).length === 0;
    if (ifPassed && resolved.then !== undefined) {
      issues.push(...validate(value, resolved.then, rootSchema, path, registry));
    } else if (!ifPassed && resolved.else !== undefined) {
      issues.push(...validate(value, resolved.else, rootSchema, path, registry));
    }
  }
  return issues;
}

/**
 * The set of declared types after handling array form and stripping
 * `null` for the nullable-union case. An empty array means "no `type`
 * keyword was set" (typeless schema).
 */
function effectiveTypes(type: JsonSchema['type']): JsonSchemaType[] {
  if (type === undefined) return [];
  if (typeof type === 'string') return [type];
  // Array form. For nullable unions (['T', 'null']), strip 'null' here
  // because non-null values are handled by the type-matching path. For
  // general unions (['string', 'number']), keep all entries so the
  // matcher can pick the right one.
  return type;
}

function valueIsOfType(value: JsonValue, type: JsonSchemaType): boolean {
  switch (type) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number' && !Number.isNaN(value);
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'null':
      return value === null;
    case 'array':
      return Array.isArray(value);
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}

function schemaAllowsNull(schema: JsonSchema): boolean {
  const t = schema.type;
  if (t === 'null') return true;
  if (Array.isArray(t) && t.includes('null')) return true;
  return false;
}

/**
 * Build a friendly "Expected X" / "Expected X or Y" message for type
 * mismatches. The wording matches what the editor's leaf-mismatch
 * messages used to say in the single-type case, extended to unions.
 */
function expectedMessage(types: JsonSchemaType[]): string {
  const friendly = (t: JsonSchemaType): string => {
    switch (t) {
      case 'string':
        return 'text';
      case 'number':
        return 'a number';
      case 'integer':
        return 'a whole number';
      case 'boolean':
        return 'true or false';
      case 'null':
        return 'null';
      case 'array':
        return 'a list';
      case 'object':
        return 'an object';
    }
  };
  if (types.length === 1) return `Expected ${friendly(types[0]!)}`;
  if (types.length === 2) return `Expected ${friendly(types[0]!)} or ${friendly(types[1]!)}`;
  const init = types.slice(0, -1).map(friendly).join(', ');
  return `Expected ${init}, or ${friendly(types[types.length - 1]!)}`;
}

function jsonEqual(a: JsonValue, b: JsonValue): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => jsonEqual(v, b[i]!));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((k) =>
      jsonEqual((a as Record<string, JsonValue>)[k]!, (b as Record<string, JsonValue>)[k]!),
    );
  }
  return false;
}
